import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { listEnvironmentStatuses, type EnvironmentStatusEntry } from "@/lib/api";

/** デバイスの環境があるときに、デバイスのオンラインを取り直す間隔。ランナーのハートビートと同じ。 */
export const RUNNER_ONLINE_POLL_INTERVAL_MS = 15_000;

/**
 * 既存の行のデバイスのオンラインだけを、取り直した値に合わせる。状態は合わせない (起動中のカードが持つ遷移中の状態を、
 * サーバーのまだ起動していない状態で上書きしてバッジがちらつかないように)。変わらなければ同じ Map を返す。
 */
export function withRunnerOnline(
	statuses: Map<string, EnvironmentStatusEntry>,
	entries: EnvironmentStatusEntry[],
): Map<string, EnvironmentStatusEntry> {
	let next = statuses;
	for (const { environmentId, runnerOnline } of entries) {
		const current = statuses.get(environmentId);
		if (!current || runnerOnline === undefined || current.runnerOnline === runnerOnline) continue;
		if (next === statuses) next = new Map(statuses);
		next.set(environmentId, { ...current, runnerOnline });
	}
	return next;
}

/**
 * <code>enabled</code> の間 (デバイスの環境があるとき) だけ状態を定期的に取り直し、カードのデバイスのオンラインのバッジを
 * 再読み込みなしに更新する。クラウドの環境だけなら取り直さない (状態の一覧は環境 Pod の全件列挙を伴うため)。
 * @returns 接続状態の更新に失敗している間は true。
 */
export function useRunnerOnlinePolling(
	enabled: boolean,
	setStatuses: Dispatch<SetStateAction<Map<string, EnvironmentStatusEntry>>>,
): boolean {
	const [failed, setFailed] = useState(false);
	useEffect(() => {
		setFailed(false);
		if (!enabled) return;
		const controller = new AbortController();
		const timer = setInterval(() => {
			listEnvironmentStatuses(controller.signal)
				.then((entries) => {
					if (controller.signal.aborted) return;
					setStatuses((prev) => withRunnerOnline(prev, entries));
					setFailed(false);
				})
				.catch((err: unknown) => {
					if (controller.signal.aborted) return;
					console.error("デバイスの接続状態を更新できませんでした", err);
					setFailed(true);
				});
		}, RUNNER_ONLINE_POLL_INTERVAL_MS);
		return () => {
			clearInterval(timer);
			controller.abort();
		};
	}, [enabled, setStatuses]);
	return enabled && failed;
}
